import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { Calendar, DollarSign, Eye, Download, Search, Trash2, FileText, XCircle, Phone, MapPin, User, Image, Receipt, RotateCcw, Camera, X, MessageCircle, Upload, BadgeCheck, History, Hash, FileDown } from 'lucide-react';
import { toast } from 'sonner';
import NivelBadge from '../components/NivelBadge';


const BACKEND_URL = window.location.origin;
const API = `${BACKEND_URL}/api`;

export default function DividasPage() {
  const [promissorias, setPromissorias] = useState([]);
  const [promissoriasFiltradas, setPromissoriasFiltradas] = useState([]);
  const [loading, setLoading] = useState(false);
  const [busca, setBusca] = useState('');
  const [filtroStatus, setFiltroStatus] = useState('ativa');

  const [showPreview, setShowPreview] = useState(false);
  const [selectedDetalhes, setSelectedDetalhes] = useState(null);
  const pedidoRef = React.useRef(null);
  const [pagamentoDialogOpen, setPagamentoDialogOpen] = useState(false);
  const [promissoriaPagamento, setPromissoriaPagamento] = useState(null);
  const [pagamentos, setPagamentos] = useState([]);
  const [formPagamento, setFormPagamento] = useState({ forma_pagamento: 'PIX', valor: '', data_pagamento: new Date().toISOString().split('T')[0], imagem_path: '' });
  const [uploadingAdiantamento, setUploadingAdiantamento] = useState(false);
  const [recibosDialog, setRecibosDialog] = useState(false);
  const [recibosData, setRecibosData] = useState([]);
  const [recibosCliente, setRecibosCliente] = useState('');
  const [carros, setCarros] = useState([]);
  const [filtroCarro, setFiltroCarro] = useState('');  // '' = todos
  const [uploadingFoto, setUploadingFoto] = useState(null); // promissoria_id sendo enviada
  const [fotoViewer, setFotoViewer] = useState(null); // { promId, fotos: [], idx, blobUrl }
  const [loadingFoto, setLoadingFoto] = useState(false);
  // Reset contador
  const [resetContadorOpen, setResetContadorOpen] = useState(false);
  const [resetPin, setResetPin] = useState('');
  const [resetStartFrom, setResetStartFrom] = useState('1');
  const [resetStatus, setResetStatus] = useState(null); // { reset_ativo, next_number, comeca_de, reset_em }
  const [resetSaving, setResetSaving] = useState(false);
  const fotoInputRefs = React.useRef({});

  const user = JSON.parse(localStorage.getItem('user') || '{}');

  const isAdmin = user.tipo === 'admin' || user.tipo === 'secretario' || !user.tipo;
  const isSecretario = user.tipo === 'secretario';
  const isVendedor = user.tipo === 'vendedor';

  const formatarMoeda = (v) => `R$ ${(Number(v) || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

  const [, setTick] = useState(0);
  useEffect(() => {
    if (!isVendedor) return;
    const interval = setInterval(() => setTick(t => t + 1), 30000);
    return () => clearInterval(interval);
  }, [isVendedor]);

  const podeCancelar = (p) => {
    if (isAdmin) return true;
    if (!isVendedor) return false;
    if (!p.created_at) return false;
    const created = new Date(p.created_at);
    const agora = new Date();
    return (agora - created) < 600000; // 10 minutos
  };

  // Editável: ativa/observacao. Admin: sempre (sem janela). Demais: dentro de 12h da criação.
  const podeEditar = (p) => {
    if (p.status === 'quitada' || p.status === 'cancelada') return false;
    if (user.tipo === 'admin') return true;
    if (!p.created_at) return false;
    const created = new Date(p.created_at);
    const agora = new Date();
    const diffH = (agora - created) / 3600000;
    return diffH <= 12;
  };

  const navigate = useNavigate();
  const handleEditarNota = (p) => {
    navigate(`/nova-promissoria?editar=${p.id}`);
  };

  useEffect(() => {
    carregarPromissorias();
    if (isAdmin) loadCarros();
  }, [filtroCarro]);

  const loadCarros = async () => {
    try { const { data } = await axios.get(`${API}/carros`); setCarros(Array.isArray(data) ? data : []); } catch {}
  };

  const carregarPromissorias = async () => {
    try {
      setLoading(true);
      const params = {};
      if (filtroCarro) params.carro_id = filtroCarro;
      const response = await axios.get(`${API}/promissorias`, { params });
      setPromissorias(response.data);
      // Aplica filtro atual (default 'ativas') sobre a lista recém-carregada
      // Não podemos usar aplicarFiltros() aqui porque ela lê `promissorias` do state antigo.
      let resultado = [...response.data];
      if (filtroStatus !== 'todas') {
        resultado = resultado.filter(p => getStatusLabel(p) === filtroStatus);
      }
      setPromissoriasFiltradas(resultado);
      setBusca('');
    } catch (error) {
      toast.error("Erro ao carregar promissorias");
    } finally {
      setLoading(false);
    }
  };

  const handleFotoUpload = async (promId, file) => {
    if (!file) return;
    setUploadingFoto(promId);
    try {
      const form = new FormData();
      form.append("file", file);
      const { data } = await axios.post(`${API}/promissorias/${promId}/foto`, form, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Foto enviada!");
      // Atualizar a promissória localmente
      setPromissorias(prev => prev.map(p => p.id === promId ? { ...p, fotos: [...(p.fotos || []), data] } : p));
      setPromissoriasFiltradas(prev => prev.map(p => p.id === promId ? { ...p, fotos: [...(p.fotos || []), data] } : p));
      if (selectedDetalhes?.id === promId) {
        setSelectedDetalhes(prev => ({ ...prev, fotos: [...(prev.fotos || []), data] }));
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erro ao enviar foto");
    } finally {
      setUploadingFoto(null);
    }
  };

  const triggerFotoInput = (promId) => {
    const input = fotoInputRefs.current[promId];
    if (input) { input.value = ''; input.click(); }
  };

  const abrirFotoViewer = async (prom, idx = 0) => {
    const fotos = prom.fotos || [];
    if (fotos.length === 0) return;
    setFotoViewer({ promId: prom.id, fotos, idx, blobUrl: null });
    setLoadingFoto(true);
    try {
      const resp = await axios.get(`${API}${fotos[idx].url.replace('/api', '')}`, { responseType: 'blob' });
      const blobUrl = URL.createObjectURL(resp.data);
      setFotoViewer(v => v ? { ...v, blobUrl } : v);
    } catch (e) {
      toast.error("Erro ao carregar foto");
    } finally {
      setLoadingFoto(false);
    }
  };

  const navegarFoto = async (delta) => {
    if (!fotoViewer) return;
    const novoIdx = fotoViewer.idx + delta;
    if (novoIdx < 0 || novoIdx >= fotoViewer.fotos.length) return;
    if (fotoViewer.blobUrl) URL.revokeObjectURL(fotoViewer.blobUrl);
    setFotoViewer(v => ({ ...v, idx: novoIdx, blobUrl: null }));
    setLoadingFoto(true);
    try {
      const resp = await axios.get(`${API}${fotoViewer.fotos[novoIdx].url.replace('/api', '')}`, { responseType: 'blob' });
      const blobUrl = URL.createObjectURL(resp.data);
      setFotoViewer(v => v ? { ...v, blobUrl } : v);
    } catch {
      toast.error("Erro ao carregar foto");
    } finally {
      setLoadingFoto(false);
    }
  };

  const fecharFotoViewer = () => {
    if (fotoViewer?.blobUrl) URL.revokeObjectURL(fotoViewer.blobUrl);
    setFotoViewer(null);
  };

  const excluirFoto = async () => {
    if (!fotoViewer || !isAdmin) return;
    const foto = fotoViewer.fotos[fotoViewer.idx];
    if (!window.confirm("Excluir esta foto?")) return;
    try {
      await axios.delete(`${API}/promissorias/${fotoViewer.promId}/foto/${foto.id}`);
      toast.success("Foto excluída");
      // Atualiza estado
      setPromissorias(prev => prev.map(p => p.id === fotoViewer.promId ? { ...p, fotos: (p.fotos || []).filter(f => f.id !== foto.id) } : p));
      setPromissoriasFiltradas(prev => prev.map(p => p.id === fotoViewer.promId ? { ...p, fotos: (p.fotos || []).filter(f => f.id !== foto.id) } : p));
      const fotosRestantes = fotoViewer.fotos.filter(f => f.id !== foto.id);
      if (fotosRestantes.length === 0) {
        fecharFotoViewer();
      } else {
        const novoIdx = Math.min(fotoViewer.idx, fotosRestantes.length - 1);
        if (fotoViewer.blobUrl) URL.revokeObjectURL(fotoViewer.blobUrl);
        setFotoViewer({ ...fotoViewer, fotos: fotosRestantes, idx: novoIdx, blobUrl: null });
        setLoadingFoto(true);
        const resp = await axios.get(`${API}${fotosRestantes[novoIdx].url.replace('/api', '')}`, { responseType: 'blob' });
        setFotoViewer(v => v ? { ...v, blobUrl: URL.createObjectURL(resp.data) } : v);
        setLoadingFoto(false);
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erro ao excluir");
    }
  };

  const fotoPendente = (p) => p.foto_requerida === true && p.status !== 'cancelada' && (!p.fotos || p.fotos.length === 0);

  const handleToggleNfe = async (p) => {
    try {
      const { data } = await axios.post(`${API}/promissorias/${p.id}/nfe`);
      setPromissorias(prev => prev.map(x => x.id === p.id ? { ...x, nfe_emitida: data.nfe_emitida } : x));
      setPromissoriasFiltradas(prev => prev.map(x => x.id === p.id ? { ...x, nfe_emitida: data.nfe_emitida } : x));
      toast.success(data.nfe_emitida ? "NFe marcada como emitida" : "NFe desmarcada");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erro ao alterar NFe");
    }
  };

  const getStatusLabel = (p) => {
    if (p.status === 'quitada') return 'quitada';
    if ((p.status === 'ativa' || p.status === 'observacao') && isRecobranca(p.data_vencimento)) return 'recobranca';
    if (p.status === 'ativa' && isVencida(p.data_vencimento)) return 'vencida';
    if (p.status === 'observacao') return 'parcial';
    return 'ativa';
  };

  const aplicarFiltros = (texto, status) => {
    let resultado = [...promissorias];
    if (texto.trim()) {
      const t = texto.toLowerCase();
      resultado = resultado.filter(p =>
        p.cliente_nome?.toLowerCase().includes(t) || p.numero_pedido?.toLowerCase().includes(t)
      );
    }
    if (status !== 'todas') {
      resultado = resultado.filter(p => getStatusLabel(p) === status);
    }
    setPromissoriasFiltradas(resultado);
  };

  const handleBuscar = (texto) => {
    setBusca(texto);
    aplicarFiltros(texto, filtroStatus);
  };

  const handleFiltroStatus = (status) => {
    setFiltroStatus(status);
    aplicarFiltros(busca, status);
  };

  const handleViewDetalhes = async (promissoria) => {
    try {
      const response = await axios.get(`${API}/promissorias/${promissoria.id}/detalhes`);
      setSelectedDetalhes(response.data);
      setShowPreview(true);
    } catch (error) {
      toast.error("Erro ao carregar detalhes");
    }
  };

  const handleDownloadPDF = async (promissoria) => {
    try {
      const response = await axios.get(`${API}/promissorias/${promissoria.id}/pdf`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `Recibo_${promissoria.numero_pedido}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success("PDF baixado!");
    } catch (error) {
      toast.error("Erro ao baixar PDF");
    }
  };

  const handleOpenPagamento = async (promissoria) => {
    setPromissoriaPagamento(promissoria);
    setPagamentoDialogOpen(true);
    setFormPagamento({ forma_pagamento: 'PIX', valor: '', data_pagamento: new Date().toISOString().split('T')[0], imagem_path: '' });
    try {
      const response = await axios.get(`${API}/promissorias/${promissoria.id}/pagamentos`);
      setPagamentos(response.data.pagamentos || []);
    } catch (error) {
      setPagamentos([]);
    }
  };

  const uploadAdiantamentoFile = async (file) => {
    if (!file) return;
    setUploadingAdiantamento(true);
    try {
      const fd = new FormData();
      fd.append('imagem', file);
      const { data } = await axios.post(`${API}/comprovantes/upload`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setFormPagamento(prev => ({ ...prev, imagem_path: data.imagem_path }));
      toast.success('Comprovante enviado!');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erro ao enviar comprovante');
    } finally {
      setUploadingAdiantamento(false);
    }
  };

  const handleViewRecibos = async (promissoria) => {
    try {
      const response = await axios.get(`${API}/promissorias/${promissoria.id}/pagamentos`);
      const pags = response.data.pagamentos || [];
      const comImagem = pags.filter(p => p.imagem_path);
      if (comImagem.length === 0) {
        toast.info("Esta promissória não possui recibos com imagem");
        return;
      }
      setRecibosData(comImagem);
      setRecibosCliente(promissoria.cliente_nome);
      setRecibosDialog(true);
    } catch (error) {
      toast.error("Erro ao carregar recibos");
    }
  };

  const handleInvalidarComprovanteDividas = async (pagamento_id) => {
    // Toggle invalidar/reativar comprovante (mesma lógica do Caixa do Carro).
    // Backend recalcula valor_pago e status; frontend recarrega lista + detalhes + recibos.
    try {
      const { data } = await axios.post(`${API}/caixa/pagamento/${pagamento_id}/invalidar`);
      toast.success(data.invalido ? "Comprovante invalidado" : "Comprovante reativado");
      // Recarrega a lista principal
      await carregarPromissorias();
      // Atualiza estado local dos recibos (mantém dialog aberto)
      if (promissoriaPagamento?.id) {
        try {
          const res = await axios.get(`${API}/promissorias/${promissoriaPagamento.id}/pagamentos`);
          const pags = res.data.pagamentos || [];
          setPagamentos(pags);
          setRecibosData(pags.filter(p => p.imagem_path));
        } catch { /* silencioso: mantém estado local se GET falhar */ }
      } else if (recibosData.length > 0) {
        // Atualiza flag local do recibo aberto
        setRecibosData(prev => prev.map(p => p.id === pagamento_id ? { ...p, invalido: data.invalido } : p));
      }
      // Se o detalhe está aberto, também atualiza a foto/pagamentos
      if (selectedDetalhes?.id) {
        try {
          const { data: prom } = await axios.get(`${API}/promissorias/${selectedDetalhes.id}`);
          setSelectedDetalhes(prom);
        } catch { /* silencioso */ }
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erro ao invalidar comprovante");
    }
  };

  const handleDownloadRecibo = async (imagem_path) => {
    try {
      const response = await axios.get(`${API}/caixa/imagem/${imagem_path}`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(response.data);
      const link = document.createElement('a');
      link.href = url;
      const ext = imagem_path.split('.').pop() || 'jpg';
      link.download = `recibo_${Date.now()}.${ext}`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      toast.error("Erro ao baixar arquivo");
    }
  };

  const isPdf = (path) => path && path.toLowerCase().endsWith('.pdf');

  const RenderComprovante = ({ path, alt }) => {
    if (!path) return null;
    if (isPdf(path)) {
      return <iframe src={`${API}/caixa/imagem/${path}`} title={alt} className="w-full h-80 border-0" />;
    }
    return <img src={`${API}/caixa/imagem/${path}`} alt={alt} className="w-full max-h-80 object-contain bg-white" onError={(e) => { e.target.style.display = 'none'; }} />;
  };

  const handleAddPagamento = async (e) => {
    e.preventDefault();
    if (!promissoriaPagamento) return;
    if (!formPagamento.imagem_path) {
      toast.error("Anexe o comprovante (foto ou upload)");
      return;
    }
    try {
      const response = await axios.post(`${API}/promissorias/${promissoriaPagamento.id}/pagamentos`, {
        forma_pagamento: formPagamento.forma_pagamento,
        valor: parseFloat(formPagamento.valor),
        data_pagamento: formPagamento.data_pagamento,
        imagem_path: formPagamento.imagem_path,
      });
      toast.success("Pagamento adicionado!");
      setPagamentos(response.data.pagamentos || []);
      setFormPagamento({ forma_pagamento: 'PIX', valor: '', data_pagamento: new Date().toISOString().split('T')[0], imagem_path: '' });
      carregarPromissorias();
      if (response.data.status === "quitada") {
        toast.success("Promissoria quitada!");
        setPagamentoDialogOpen(false);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erro ao adicionar pagamento");
    }
  };

  // Senha + lixeira
  const [excluirDialog, setExcluirDialog] = useState(null); // {promissoria}
  const [senhaExcluir, setSenhaExcluir] = useState("");
  const [excluindoLoading, setExcluindoLoading] = useState(false);
  const [lixeira, setLixeira] = useState([]);
  const [showLixeira, setShowLixeira] = useState(false);
  const [purgaSenha, setPurgaSenha] = useState({ open: null, senha: "" });

  const handleDeletePromissoria = (promissoria) => {
    setSenhaExcluir("");
    setExcluirDialog({ promissoria });
  };

  const confirmarExclusao = async () => {
    if (!senhaExcluir) { toast.error("Digite a senha"); return; }
    setExcluindoLoading(true);
    try {
      await axios.delete(`${API}/promissorias/${excluirDialog.promissoria.id}`, { data: { senha: senhaExcluir } });
      toast.success("Nota movida para a lixeira (7 dias)");
      setExcluirDialog(null);
      setSenhaExcluir("");
      setShowPreview(false);
      carregarPromissorias();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erro ao excluir");
    } finally { setExcluindoLoading(false); }
  };

  const carregarLixeira = async () => {
    try {
      const { data } = await axios.get(`${API}/promissorias-lixeira`);
      setLixeira(data || []);
    } catch (e) { toast.error("Erro ao carregar lixeira"); }
  };

  const restaurarDaLixeira = async (prom) => {
    try {
      await axios.post(`${API}/promissorias/${prom.id}/restaurar`);
      toast.success(`Nota restaurada`);
      await carregarLixeira();
      carregarPromissorias();
    } catch (e) { toast.error(e?.response?.data?.detail || "Erro ao restaurar"); }
  };

  const purgarPermanente = async (prom) => {
    try {
      await axios.delete(`${API}/promissorias/${prom.id}/purgar`);
      toast.success("Nota apagada definitivamente");
      setPurgaSenha({ open: null, senha: "" });
      await carregarLixeira();
    } catch (e) { toast.error(e?.response?.data?.detail || "Erro ao apagar"); }
  };

  const handleCancelarPromissoria = async (promissoria) => {
    try {
      await axios.post(`${API}/promissorias/${promissoria.id}/cancelar`);
      toast.success("Promissoria cancelada! Estoque revertido.");
      carregarPromissorias();
      setShowPreview(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erro ao cancelar");
    }
  };

  const [downloadingImage, setDownloadingImage] = useState(false);

  const handleDownloadImage = async () => {
    if (!selectedDetalhes) return;
    setDownloadingImage(true);
    try {
      const token = localStorage.getItem('token');
      const resp = await fetch(`${API}/promissorias/${selectedDetalhes.id}/pdf-a4`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (!resp.ok) throw new Error('Erro ao gerar PDF');
      const blob = await resp.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `Pedido_${selectedDetalhes.numero_pedido}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success("PDF baixado!");
    } catch (error) {
      toast.error("Erro ao gerar PDF");
    } finally {
      setDownloadingImage(false);
    }
  };

  const isVencida = (dv) => dv < new Date().toISOString().split('T')[0];
  const isRecobranca = (dv) => {
    if (!dv) return false;
    const hoje = new Date();
    const venc = new Date(dv + 'T00:00:00');
    const diffDias = Math.floor((hoje - venc) / (1000 * 60 * 60 * 24));
    return diffDias > 8;
  };
  const formatDate = (ds) => { if (!ds) return ''; const [y, m, d] = ds.split('-'); return `${d}/${m}/${y}`; };
  const toCGTime = (iso) => { if (!iso) return ''; try { return new Date(iso).toLocaleString('pt-BR', { timeZone: 'America/Campo_Grande', hour: '2-digit', minute: '2-digit' }); } catch { return ''; } };

  const calcularTotais = () => promissoriasFiltradas.reduce((acc, p) => {
    acc.total_valor += p.valor_total || 0;
    acc.total_quitado += p.status === 'quitada' ? (p.valor_total || 0) : (p.valor_pago || 0);
    acc.total_debito += p.status !== 'quitada' ? ((p.valor_total || 0) - (p.valor_pago || 0)) : 0;
    return acc;
  }, { total_valor: 0, total_quitado: 0, total_debito: 0 });

  const totais = calcularTotais();

  const carregarStatusContador = async () => {
    try {
      const { data } = await axios.get(`${API}/promissorias/next-number`);
      setResetStatus(data);
    } catch (e) {
      setResetStatus(null);
    }
  };

  const aplicarResetContador = async () => {
    const sf = parseInt(resetStartFrom, 10);
    if (!sf || sf < 1) { toast.error('Informe um número inicial válido (mínimo 1)'); return; }
    setResetSaving(true);
    try {
      const { data } = await axios.post(`${API}/promissorias/resetar-contador`, { pin: resetPin, start_from: sf });
      toast.success(data.message || 'Contador resetado!');
      setResetPin('');
      await carregarStatusContador();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Erro ao resetar contador');
    } finally {
      setResetSaving(false);
    }
  };

  const desfazerResetContador = async () => {
    if (!window.confirm('Voltar ao comportamento padrão (próximo número = maior existente + 1)?')) return;
    setResetSaving(true);
    try {
      await axios.delete(`${API}/promissorias/resetar-contador`, { data: { pin: resetPin || '000' } });
      toast.success('Reset desfeito');
      setResetPin('');
      await carregarStatusContador();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Erro ao desfazer');
    } finally {
      setResetSaving(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-start gap-3 flex-wrap">
        <div>
          <h2 className="text-3xl font-bold" style={{fontFamily: 'Work Sans'}}>Dividas e Promissorias</h2>
          <p className="text-gray-600 mt-1">Visualize e gerencie todas as promissorias</p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <Button
            variant="outline"
            onClick={() => {
              const token = localStorage.getItem("token");
              fetch(`${API}/promissorias/recobrancas/pdf`, { headers: { Authorization: `Bearer ${token}` } })
                .then(r => { if (!r.ok) throw new Error(); return r.blob(); })
                .then(b => {
                  const u = URL.createObjectURL(b);
                  window.open(u, "_blank");
                  setTimeout(() => URL.revokeObjectURL(u), 60000);
                })
                .catch(() => toast.error("Erro ao gerar PDF de recobranças"));
            }}
            className="gap-2 text-red-700 border-red-300 hover:bg-red-50"
            data-testid="btn-recobrancas-pdf"
          >
            <FileDown className="h-4 w-4" /> PDF Recobranças
          </Button>
          {/* Botão "Mostrar Histórico" removido — filtro Todas/Ativas/Quitadas/Vencidas/Recobrança do card já cobre isso */}
          {isAdmin && (
            <>
              <Button variant="outline" onClick={() => { setShowLixeira(true); carregarLixeira(); }} className="gap-2 text-gray-700 border-gray-300" data-testid="btn-abrir-lixeira">
                <Trash2 className="h-4 w-4" /> Lixeira (7 dias)
              </Button>
              <Button variant="outline" onClick={() => { setResetContadorOpen(true); carregarStatusContador(); }} className="gap-2 text-amber-700 border-amber-300 hover:bg-amber-50" data-testid="btn-resetar-contador">
                <Hash className="h-4 w-4" /> Resetar Contador
              </Button>
            </>
          )}
        </div>
      </div>

      <Card>
        <CardContent className="pt-6">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
            <Input type="text" placeholder="Nome do cliente ou numero do pedido..." className="pl-10"
              value={busca} onChange={(e) => handleBuscar(e.target.value)} data-testid="busca-promissoria" />
          </div>
          <div className="flex flex-wrap gap-2 mt-4">
            {[
              { key: 'todas', label: 'Todas', color: 'bg-gray-100 text-gray-700 hover:bg-gray-200', active: 'bg-gray-700 text-white' },
              { key: 'ativa', label: 'Ativas', color: 'bg-blue-50 text-blue-700 hover:bg-blue-100', active: 'bg-blue-600 text-white' },
              { key: 'vencida', label: 'Vencidas', color: 'bg-red-50 text-red-700 hover:bg-red-100', active: 'bg-red-500 text-white' },
              { key: 'recobranca', label: 'Recobranca', color: 'bg-red-50 text-red-900 hover:bg-red-100', active: 'bg-red-800 text-white' },
              { key: 'parcial', label: 'Parcial', color: 'bg-yellow-50 text-yellow-700 hover:bg-yellow-100', active: 'bg-yellow-500 text-white' },
              { key: 'quitada', label: 'Quitadas', color: 'bg-green-50 text-green-700 hover:bg-green-100', active: 'bg-green-500 text-white' },
            ].map(f => (
              <button key={f.key} data-testid={`filtro-${f.key}`}
                onClick={() => handleFiltroStatus(f.key)}
                className={`px-3 py-1.5 rounded-full text-sm font-medium transition-colors ${filtroStatus === f.key ? f.active : f.color}`}>
                {f.label}
                {f.key !== 'todas' && <span className="ml-1 opacity-75">({promissorias.filter(p => getStatusLabel(p) === f.key).length})</span>}
              </button>
            ))}
          </div>
          {isAdmin && carros.length > 0 && (
            <div className="flex flex-wrap gap-2 mt-3 pt-3 border-t" data-testid="filtro-carros">
              <span className="text-xs font-semibold text-gray-600 self-center mr-1">Carro:</span>
              <button
                onClick={() => setFiltroCarro('')}
                className={`px-3 py-1.5 rounded-full text-xs font-medium transition-colors ${!filtroCarro ? 'bg-indigo-600 text-white' : 'bg-indigo-50 text-indigo-700 hover:bg-indigo-100'}`}
                data-testid="filtro-carro-todos"
              >
                Todos os Carros
              </button>
              {carros.map(c => (
                <button
                  key={c.id}
                  onClick={() => setFiltroCarro(c.id)}
                  className={`px-3 py-1.5 rounded-full text-xs font-medium transition-colors ${filtroCarro === c.id ? 'bg-indigo-600 text-white' : 'bg-indigo-50 text-indigo-700 hover:bg-indigo-100'}`}
                  data-testid={`filtro-carro-${c.id}`}
                >
                  {c.nome}
                </button>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {promissoriasFiltradas.length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Card><CardContent className="pt-6"><div className="flex items-center justify-between"><div><p className="text-sm text-gray-600">Total</p><p className="text-3xl font-bold">{promissoriasFiltradas.length}</p></div><FileText className="h-10 w-10 text-blue-600" /></div></CardContent></Card>
          <Card><CardContent className="pt-6"><div className="flex items-center justify-between"><div><p className="text-sm text-gray-600">Valor Total</p><p className="text-2xl font-bold text-blue-600">R$ {totais.total_valor.toLocaleString('pt-BR', {minimumFractionDigits:2})}</p></div><DollarSign className="h-10 w-10 text-blue-600" /></div></CardContent></Card>
          <Card><CardContent className="pt-6"><div><p className="text-sm text-gray-600">Quitado / Debito</p><div className="flex items-center gap-2"><p className="text-lg font-bold text-green-600">R$ {totais.total_quitado.toFixed(2)}</p><span className="text-gray-400">/</span><p className="text-lg font-bold text-red-600">R$ {totais.total_debito.toFixed(2)}</p></div></div></CardContent></Card>
        </div>
      )}

      {loading ? (
        <div className="text-center py-12"><p className="text-gray-500">Carregando...</p></div>
      ) : promissoriasFiltradas.length === 0 ? (
        <div className="text-center py-12"><FileText className="h-12 w-12 mx-auto text-gray-400" /><p className="text-gray-500 mt-4">{busca ? 'Nenhuma encontrada' : 'Nenhuma cadastrada'}</p></div>
      ) : (
        <div className="space-y-3">
          {/* Área especial: Notas Alteradas pelo vendedor — pendentes de NFe */}
          {(() => {
            const alteradas = promissoriasFiltradas.filter(p => p.alteracao_pendente);
            if (alteradas.length === 0 || !isAdmin) return null;
            return (
              <Card className="border-2 border-amber-400 bg-amber-50/40 shadow-md" data-testid="area-notas-alteradas">
                <CardContent className="pt-5">
                  <div className="flex items-center gap-2 mb-3">
                    <span className="text-2xl">⚠️</span>
                    <div className="flex-1">
                      <h3 className="text-base font-bold text-amber-900">Notas Alteradas — Pendentes de NFe</h3>
                      <p className="text-xs text-amber-800">Confira o que mudou e emita a nova NFe. Após emitir, a nota volta para a lista normal.</p>
                    </div>
                    <span className="bg-amber-500 text-white text-xs font-bold rounded-full px-2.5 py-0.5">{alteradas.length}</span>
                  </div>
                  <div className="space-y-2">
                    {alteradas.map(p => (
                      <NotaAlteradaCard
                        key={`alt-${p.id}`}
                        p={p}
                        formatarMoeda={formatarMoeda}
                        onAprovar={async () => {
                          try {
                            await axios.post(`${API}/promissorias/${p.id}/alteracao-aprovar`);
                            toast.success("Alteração aprovada — NFe marcada como emitida!");
                            setPromissorias(prev => prev.map(x => x.id === p.id ? { ...x, alteracao_pendente: false } : x));
                            setPromissoriasFiltradas(prev => prev.map(x => x.id === p.id ? { ...x, alteracao_pendente: false } : x));
                          } catch (err) {
                            toast.error(err?.response?.data?.detail || "Erro ao aprovar");
                          }
                        }}
                      />
                    ))}
                  </div>
                </CardContent>
              </Card>
            );
          })()}

          {promissoriasFiltradas
            .slice()
            .sort((a, b) => {
              // Alteração pendente sobe pro topo (caso o usuário não veja a área especial — ex: secretário sem isAdmin)
              const aP = a.alteracao_pendente ? 0 : 1;
              const bP = b.alteracao_pendente ? 0 : 1;
              if (aP !== bP) return aP - bP;
              return 0;
            })
            .map((p) => (
            <Card key={p.id} className={`shadow-sm hover:shadow-md transition-shadow ${p.alteracao_pendente ? 'border-2 border-amber-400 ring-2 ring-amber-200/50' : ''}`} data-testid={`card-promissoria-${p.id}`}>
              <CardContent className="pt-6">
                {p.alteracao_pendente && (
                  <div className="mb-3 -mt-2 -mx-2 px-3 py-1.5 bg-amber-100 border-l-4 border-amber-500 rounded text-xs text-amber-900 font-bold flex items-center gap-1.5" data-testid={`badge-alterada-${p.id}`}>
                    ⚠️ NOTA ALTERADA{p.editada_por_nome ? ` por ${p.editada_por_nome}` : ""} — pendente NFe
                  </div>
                )}
                <div className="flex justify-between items-start mb-4">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-2">
                      <h3 className="text-lg font-bold"><NivelBadge nivel={p.cliente_nivel} className="mr-1" />{p.cliente_nome}</h3>
                      <span className="text-sm text-gray-500">N. {p.numero_pedido}</span>
                    </div>
                    <div className="flex gap-2 flex-wrap">
                      {p.status === "quitada" && <Badge className="bg-green-500">Quitada</Badge>}
                      {p.status === "observacao" && <Badge className="bg-yellow-500">Parcial</Badge>}
                      {p.status === "ativa" && isVencida(p.data_vencimento) && <Badge className="bg-red-500">Vencida</Badge>}
                      {p.status === "ativa" && !isVencida(p.data_vencimento) && <Badge className="bg-blue-500">Ativa</Badge>}
                      {p.venda_avista && <Badge className="bg-emerald-600 text-white" data-testid={`badge-avista-${p.id}`}>À Vista</Badge>}
                      {(p.status === "ativa" || p.status === "observacao") && isRecobranca(p.data_vencimento) && (
                        <Badge className="bg-red-800 text-white border border-red-900" data-testid={`badge-recobranca-${p.id}`}>Recobranca</Badge>
                      )}
                      {fotoPendente(p) && (
                        <Badge className="bg-red-600 text-white border border-red-700 animate-pulse gap-1" data-testid={`badge-foto-pendente-${p.id}`}>
                          <Camera className="h-3 w-3" /> Foto pendente
                        </Badge>
                      )}
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="text-2xl font-bold text-green-600">R$ {p.valor_total?.toFixed(2)}</p>
                    {p.valor_pago > 0 && p.status !== "quitada" && (
                      <p className="text-sm text-gray-500">Pago: R$ {p.valor_pago.toFixed(2)}</p>
                    )}
                  </div>
                </div>
                <div className="flex items-center gap-2 mb-4 pb-4 border-b">
                  <Calendar className="h-4 w-4 text-gray-400" />
                  <span className="text-sm">
                    Vencimento: {formatDate(p.data_vencimento)} | Criado: {formatDate(p.data_compra)} {toCGTime(p.created_at)}
                    {p.rota_nome && <> | <span className="font-semibold text-blue-700">Rota:</span> {p.rota_nome}</>}
                  </span>
                </div>
                <div className="flex flex-wrap gap-2">
                  {isSecretario && (
                    <button
                      type="button"
                      onClick={() => handleToggleNfe(p)}
                      data-testid={`toggle-nfe-${p.id}`}
                      className={`inline-flex items-center gap-2 rounded-full px-3 py-1 text-xs font-bold border-2 transition-colors ${p.nfe_emitida
                        ? 'bg-green-100 text-green-800 border-green-500 hover:bg-green-200'
                        : 'bg-red-100 text-red-800 border-red-500 hover:bg-red-200'}`}
                      title={p.nfe_emitida ? 'Clique para desmarcar' : 'Clique para marcar como emitida'}
                    >
                      <span className={`inline-block w-8 h-4 rounded-full relative transition-colors ${p.nfe_emitida ? 'bg-green-500' : 'bg-red-500'}`}>
                        <span className={`absolute top-0.5 w-3 h-3 bg-white rounded-full shadow transition-all ${p.nfe_emitida ? 'left-[18px]' : 'left-0.5'}`}></span>
                      </span>
                      {p.nfe_emitida ? 'NFe EMITIDA' : 'Emitir NFe'}
                    </button>
                  )}
                  <Button variant="outline" size="sm" onClick={() => handleViewDetalhes(p)} className="gap-1" data-testid={`btn-visualizar-${p.id}`}>
                    <Eye className="h-4 w-4" /> Visualizar
                  </Button>
                  <Button variant="outline" size="sm" onClick={() => handleDownloadPDF(p)} className="gap-1">
                    <Download className="h-4 w-4" /> PDF
                  </Button>
                  {/* Camera + Fotos */}
                  <input
                    type="file"
                    accept="image/*"
                    capture="environment"
                    hidden
                    ref={(el) => { fotoInputRefs.current[p.id] = el; }}
                    onChange={(e) => handleFotoUpload(p.id, e.target.files?.[0])}
                    data-testid={`foto-input-${p.id}`}
                  />
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => triggerFotoInput(p.id)}
                    disabled={uploadingFoto === p.id}
                    className={`gap-1 ${fotoPendente(p) ? 'text-red-600 border-red-300 hover:bg-red-50 animate-pulse' : 'text-indigo-600 hover:bg-indigo-50'}`}
                    data-testid={`btn-camera-${p.id}`}
                    title={fotoPendente(p) ? 'Foto da venda pendente' : 'Adicionar foto da venda'}
                  >
                    <Camera className="h-4 w-4" /> {uploadingFoto === p.id ? 'Enviando...' : 'Câmera'}
                  </Button>
                  {p.fotos && p.fotos.length > 0 && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => abrirFotoViewer(p, 0)}
                      className="gap-1 text-blue-600 hover:bg-blue-50"
                      data-testid={`btn-ver-fotos-${p.id}`}
                    >
                      <Image className="h-4 w-4" /> Fotos ({p.fotos.length})
                    </Button>
                  )}
                  {p.pagamentos && p.pagamentos.some(pg => pg.imagem_path) && (
                    <Button variant="outline" size="sm" onClick={() => handleViewRecibos(p)} className="gap-1 text-purple-600 hover:bg-purple-50" data-testid={`btn-recibos-${p.id}`}>
                      <Receipt className="h-4 w-4" /> Recibos
                    </Button>
                  )}
                  {isAdmin && (p.status === "ativa" || p.status === "observacao") && (
                    <Button size="sm" onClick={() => handleOpenPagamento(p)} className="gap-1 bg-green-500 hover:bg-green-600">
                      <DollarSign className="h-4 w-4" /> Pagar
                    </Button>
                  )}
                  {(p.status === "ativa" || p.status === "observacao") && podeEditar(p) && (
                    <Button variant="outline" size="sm" onClick={() => handleEditarNota(p)} className="gap-1 text-amber-700 border-amber-300 hover:bg-amber-50" data-testid={`btn-editar-nota-${p.id}`}>
                      <RotateCcw className="h-4 w-4" /> Alterar Nota
                    </Button>
                  )}
                  {podeCancelar(p) && (
                    <Button variant="outline" size="sm" onClick={() => handleCancelarPromissoria(p)} className="gap-1 text-orange-600 hover:bg-orange-50" data-testid={`btn-cancelar-${p.id}`}>
                      <XCircle className="h-4 w-4" /> Cancelar
                    </Button>
                  )}
                  {isAdmin && (
                    <Button variant="outline" size="sm" onClick={() => handleDeletePromissoria(p)} className="gap-1 text-red-600 hover:bg-red-50" data-testid={`btn-excluir-${p.id}`}>
                      <Trash2 className="h-4 w-4" /> Excluir
                    </Button>
                  )}
                </div>
                {/* Observações de devoluções */}
                {p.devolucoes && p.devolucoes.length > 0 && (
                  <div className="mt-3 space-y-1">
                    {p.devolucoes.map((dev, idx) => (
                      <div key={dev.id || idx} className="flex items-center gap-2 bg-yellow-50 border border-yellow-200 rounded-lg px-3 py-1.5">
                        <RotateCcw className="h-3.5 w-3.5 text-yellow-600 flex-shrink-0" />
                        <span className="text-xs text-yellow-800">
                          <strong>Devolução R$ {dev.valor?.toFixed(2)}</strong> — {dev.motivo}
                          <span className="text-yellow-600 ml-1">({dev.data?.split('T')[0]?.split('-').reverse().join('/')})</span>
                        </span>
                      </div>
                    ))}
                  </div>
                )}
                {/* Histórico de alterações */}
                {Array.isArray(p.historico_alteracoes) && p.historico_alteracoes.length > 0 && (
                  <HistoricoAlteracoesBlock historico={p.historico_alteracoes} promId={p.id} formatarMoeda={formatarMoeda} />
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Dialog Detalhes */}
      <Dialog open={showPreview} onOpenChange={setShowPreview}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" aria-describedby="detalhes-desc">
          <DialogHeader>
            <DialogTitle className="text-2xl" style={{fontFamily: 'Work Sans'}}>Visualizacao do Pedido</DialogTitle>
          </DialogHeader>
          <p id="detalhes-desc" className="sr-only">Detalhes completos do pedido</p>

          {selectedDetalhes && (
            <div className="space-y-2 pb-2">
              <div ref={pedidoRef} className="space-y-1 p-2 bg-white" style={{fontSize:'11px',lineHeight:'1.4'}}>
                {/* Cabecalho */}
                <div className="border-2 border-black rounded px-3 py-2">
                  {/* Rota */}
                  {selectedDetalhes.cliente?.rota_nome && (
                    <div className="pb-2"><span className="text-blue-600 font-bold" style={{fontSize:'9px'}}>ROTA: </span><span className="font-bold" style={{fontSize:'18px',lineHeight:'1.2'}}>{selectedDetalhes.cliente.rota_nome}</span></div>
                  )}
                  <div className={selectedDetalhes.cliente?.rota_nome ? "border-t-2 border-black pb-3 pt-2" : "pb-3"}>
                    <div className="grid grid-cols-4 gap-1">
                      <div><span className="text-blue-600 font-bold" style={{fontSize:'9px'}}>PEDIDO </span><span className="font-bold text-blue-600" style={{fontSize:'13px'}}>{selectedDetalhes.numero_pedido}</span></div>
                      <div><span className="text-blue-600 font-bold" style={{fontSize:'9px'}}>COMPRA </span><span className="font-semibold">{formatDate(selectedDetalhes.data_compra)} {toCGTime(selectedDetalhes.created_at)}</span></div>
                      <div><span className="text-blue-600 font-bold" style={{fontSize:'9px'}}>VENC. </span><span className="font-semibold">{formatDate(selectedDetalhes.data_vencimento)}</span></div>
                      <div><span className="text-blue-600 font-bold" style={{fontSize:'9px'}}>VALOR </span><span className="font-bold text-green-600" style={{fontSize:'15px'}}>R$ {selectedDetalhes.valor_total?.toFixed(2)}</span></div>
                    </div>
                  </div>
                  <div className="border-t-2 border-black py-3">
                    <div className="grid grid-cols-2 gap-1">
                      <div><span className="text-blue-600 font-bold" style={{fontSize:'9px'}}>CIDADE: </span><span className="font-bold" style={{fontSize:'18px',lineHeight:'1.2'}}>{selectedDetalhes.cliente?.cidade_estado || ''}</span></div>
                      <div><span className="text-blue-600 font-bold" style={{fontSize:'9px'}}>END: </span>{selectedDetalhes.cliente?.endereco || ''}{selectedDetalhes.cliente?.bairro ? ` - ${selectedDetalhes.cliente.bairro}` : ''}</div>
                    </div>
                    {selectedDetalhes.cliente?.endereco_secundario && (
                      <div className="mt-1"><span className="text-blue-600 font-bold" style={{fontSize:'9px'}}>END. SECUNDARIO: </span>{selectedDetalhes.cliente.endereco_secundario}</div>
                    )}
                  </div>
                  <div className="border-t-2 border-black py-3">
                    <div style={{gridTemplateColumns:'45% 25% 30%'}} className="grid gap-1">
                      <div style={{whiteSpace:'nowrap',overflow:'hidden',textOverflow:'ellipsis'}}><span className="text-blue-600 font-bold" style={{fontSize:'9px'}}>CLIENTE: </span><span className="font-bold" style={{fontSize:'12px'}}>{selectedDetalhes.cliente_nome}</span></div>
                      <div><span className="text-blue-600 font-bold" style={{fontSize:'9px'}}>CPF: </span>{selectedDetalhes.cliente?.cpf_rg || ''}</div>
                      <div><span className="text-blue-600 font-bold" style={{fontSize:'9px'}}>CEL: </span>{selectedDetalhes.cliente?.telefone || ''}</div>
                    </div>
                    {selectedDetalhes.cliente?.data_nascimento && (
                      <div className="mt-1"><span className="text-blue-600 font-bold" style={{fontSize:'9px'}}>DATA NASC: </span>{formatDate(selectedDetalhes.cliente.data_nascimento)}</div>
                    )}
                  </div>
                  {selectedDetalhes.vendedor_nome && (
                    <div className="border-t-2 border-black pt-3"><span className="text-blue-600 font-bold" style={{fontSize:'9px'}}>VENDEDOR: </span>{selectedDetalhes.vendedor_nome}</div>
                  )}
                </div>

                {/* Itens */}
                {(() => {
                  const itens = selectedDetalhes.itens || [];
                  const v = itens.filter(i => (i.tipo || 'venda') !== 'retorno');
                  const r = itens.filter(i => i.tipo === 'retorno');
                  return (
                    <div className="space-y-1">
                      {v.length > 0 && (
                        <table className="w-full border" style={{fontSize:'8px'}}>
                          <thead className="bg-gray-100"><tr><th className="text-left px-1 py-0.5 border-b">Produto</th><th className="text-center px-1 py-0.5 border-b w-8">Qt</th><th className="text-right px-1 py-0.5 border-b w-14">Unit.</th><th className="text-right px-1 py-0.5 border-b w-16">Subtotal</th></tr></thead>
                          <tbody>{v.map((i, idx) => (
                            <tr key={idx} className="border-b">
                              <td className="px-1 py-0.5">
                                {i.tipo === 'kit' && <span className="inline-block bg-pink-100 text-pink-800 font-bold rounded px-1 mr-1" style={{fontSize:'7px'}}>KIT</span>}
                                {i.produto_nome}
                              </td>
                              <td className="text-center px-1 py-0.5">{i.quantidade}</td>
                              <td className="text-right px-1 py-0.5">R${i.preco_unitario?.toFixed(2)}</td>
                              <td className="text-right px-1 py-0.5 font-semibold">R${i.subtotal?.toFixed(2)}</td>
                            </tr>
                          ))}</tbody>
                        </table>
                      )}
                      {r.length > 0 && (
                        <table className="w-full border border-orange-200" style={{fontSize:'8px'}}>
                          <thead className="bg-orange-50"><tr><th className="text-left px-1 py-0.5 border-b" colSpan={2}>RETORNO</th><th className="text-right px-1 py-0.5 border-b w-14">Unit.</th><th className="text-right px-1 py-0.5 border-b w-16">Desc.</th></tr></thead>
                          <tbody>{r.map((i, idx) => (
                            <tr key={idx} className="border-b border-orange-100"><td className="px-1 py-0.5 text-orange-700">{i.produto_nome}</td><td className="text-center px-1 py-0.5 w-8">{i.quantidade}</td><td className="text-right px-1 py-0.5">R${i.preco_unitario?.toFixed(2)}</td><td className="text-right px-1 py-0.5 font-semibold text-orange-600">-R${i.subtotal?.toFixed(2)}</td></tr>
                          ))}</tbody>
                        </table>
                      )}
                    </div>
                  );
                })()}

                {/* Pagamentos */}
                {selectedDetalhes.pagamentos && selectedDetalhes.pagamentos.length > 0 && (
                  <div className="border rounded px-2 py-1">
                    <div className="font-bold text-green-700 border-b pb-0.5 mb-0.5" style={{fontSize:'8px'}}>PAGAMENTOS</div>
                    {selectedDetalhes.pagamentos.map((pag, idx) => {
                      const inv = !!pag.invalido;
                      return (
                        <div key={idx} className={`flex justify-between border-b border-gray-100 py-0.5 ${inv ? 'opacity-40 line-through' : ''}`} style={{fontSize:'8px'}}>
                          <span>{pag.forma_pagamento}{inv && <span className="ml-1 text-red-600 no-underline font-bold">[INVÁLIDO]</span>}</span>
                          <span className="text-gray-500">{pag.data_pagamento ? (pag.data_pagamento.includes('T') ? pag.data_pagamento.split('T')[0].split('-').reverse().join('/') : pag.data_pagamento.split('-').reverse().join('/')) : ''}</span>
                          <span className="font-semibold text-green-600">R$ {pag.valor?.toFixed(2)}</span>
                        </div>
                      );
                    })}
                    <div className="flex justify-between pt-0.5 font-bold" style={{fontSize:'8px'}}>
                      <span>TOTAL PAGO</span>
                      <span className="text-green-700">R$ {selectedDetalhes.pagamentos.filter(p => !p.invalido).reduce((s, p) => s + (p.valor || 0), 0).toFixed(2)}</span>
                    </div>
                    <div className="flex justify-between pt-0.5 font-bold border-t" style={{fontSize:'8px'}}>
                      <span>RESTANTE A PAGAR</span>
                      <span className="text-red-600">R$ {((selectedDetalhes.valor_total || 0) - selectedDetalhes.pagamentos.filter(p => !p.invalido).reduce((s, p) => s + (p.valor || 0), 0)).toFixed(2)}</span>
                    </div>
                  </div>
                )}

                {/* Observacoes - quadro branco */}
                <div className="border rounded" style={{minHeight:'60px'}}></div>
              </div>

              {/* Acoes */}
              <div className="flex gap-1.5">
                <Button size="sm" onClick={() => handleDownloadPDF(selectedDetalhes)} className="flex-1 gap-1 bg-blue-500 hover:bg-blue-600" style={{fontSize:'10px'}} data-testid="detail-download-pdf">
                  <Download className="h-3 w-3" /> PDF
                </Button>
                <Button size="sm" onClick={handleDownloadImage} disabled={downloadingImage} className="flex-1 gap-1 bg-gray-700 hover:bg-gray-800" style={{fontSize:'10px'}} data-testid="detail-download-image">
                  <FileText className="h-3 w-3" /> {downloadingImage ? 'Gerando...' : 'PDF A4'}
                </Button>
                {podeCancelar(selectedDetalhes) && (
                  <Button size="sm" variant="outline" onClick={() => handleCancelarPromissoria(selectedDetalhes)} className="flex-1 gap-1 text-orange-600 border-orange-300 hover:bg-orange-50" style={{fontSize:'10px'}} data-testid="detail-cancelar">
                    <XCircle className="h-3 w-3" /> Cancelar
                  </Button>
                )}
                {isAdmin && (
                  <Button size="sm" variant="outline" onClick={() => handleDeletePromissoria(selectedDetalhes)} className="gap-1 text-red-600 border-red-300 hover:bg-red-50" style={{fontSize:'10px'}} data-testid="detail-excluir">
                    <Trash2 className="h-3 w-3" /> Excluir
                  </Button>
                )}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Dialog Pagamento */}
      <Dialog open={pagamentoDialogOpen} onOpenChange={setPagamentoDialogOpen}>
        <DialogContent className="max-w-md" aria-describedby="pag-desc">
          <DialogHeader><DialogTitle>Adicionar Pagamento</DialogTitle></DialogHeader>
          <p id="pag-desc" className="sr-only">Formulario de pagamento</p>
          {promissoriaPagamento && (
            <div className="space-y-4">
              <div className="bg-gray-50 p-3 rounded-md space-y-1">
                <p className="text-sm"><span className="font-semibold">Cliente:</span> {promissoriaPagamento.cliente_nome}</p>
                <p className="text-sm"><span className="font-semibold">Total:</span> R$ {promissoriaPagamento.valor_total?.toFixed(2)}</p>
                <p className="text-sm"><span className="font-semibold">Pago:</span> R$ {(promissoriaPagamento.valor_pago || 0).toFixed(2)}</p>
                <p className="text-sm"><span className="font-semibold">Restante:</span> R$ {((promissoriaPagamento.valor_total || 0) - (promissoriaPagamento.valor_pago || 0)).toFixed(2)}</p>
              </div>
              {pagamentos.length > 0 && (
                <div>
                  <h4 className="font-semibold mb-2">Pagamentos:</h4>
                  {pagamentos.map((pag, i) => (
                    <div key={i} className="flex justify-between bg-gray-50 p-2 rounded mb-1">
                      <div>
                        <span className="text-sm">{pag.forma_pagamento}</span>
                        {pag.data_pagamento && <span className="text-xs text-gray-500 ml-2">{pag.data_pagamento.includes('T') ? pag.data_pagamento.split('T')[0].split('-').reverse().join('/') : pag.data_pagamento.split('-').reverse().join('/')}</span>}
                      </div>
                      <span className="text-sm font-semibold">R$ {pag.valor.toFixed(2)}</span>
                    </div>
                  ))}
                </div>
              )}
              <form onSubmit={handleAddPagamento} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium mb-1">Forma *</label>
                  <select value={formPagamento.forma_pagamento} onChange={(e) => setFormPagamento({...formPagamento, forma_pagamento: e.target.value})}
                    required className="w-full px-3 py-2 border border-gray-300 rounded-md">
                    <option value="PIX">PIX</option>
                    <option value="DH">Dinheiro</option>
                    <option value="CR">Credito</option>
                    <option value="DB">Debito</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Data do Pagamento *</label>
                  <input type="date" value={formPagamento.data_pagamento} onChange={(e) => setFormPagamento({...formPagamento, data_pagamento: e.target.value})}
                    required className="w-full px-3 py-2 border border-gray-300 rounded-md" />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Valor *</label>
                  <input type="number" step="0.01" min="0.01"
                    max={(promissoriaPagamento.valor_total || 0) - (promissoriaPagamento.valor_pago || 0)}
                    value={formPagamento.valor} onChange={(e) => setFormPagamento({...formPagamento, valor: e.target.value})}
                    required placeholder="0.00" className="w-full px-3 py-2 border border-gray-300 rounded-md" />
                </div>

                {/* Comprovante obrigatório (foto ou upload) */}
                <div>
                  <label className="block text-sm font-medium mb-1">
                    Comprovante <span className="text-red-600">*</span>
                  </label>
                  {formPagamento.imagem_path ? (
                    <div className="flex items-center gap-2 bg-emerald-50 border border-emerald-200 rounded-md px-2 py-1.5">
                      <BadgeCheck className="h-4 w-4 text-emerald-600 shrink-0" />
                      <span className="text-xs text-emerald-800 flex-1 truncate">Comprovante enviado</span>
                      <Button
                        type="button"
                        size="sm"
                        variant="ghost"
                        className="h-7 px-2 text-red-600"
                        onClick={() => setFormPagamento(prev => ({ ...prev, imagem_path: '' }))}
                        data-testid="btn-remover-comp-adiantamento"
                        title="Trocar"
                      >
                        <X className="h-3.5 w-3.5" />
                      </Button>
                    </div>
                  ) : (
                    <div className="grid grid-cols-2 gap-2">
                      <input
                        type="file"
                        accept="image/*,application/pdf"
                        capture="environment"
                        id="adiantamento-camera-input"
                        hidden
                        onChange={(e) => uploadAdiantamentoFile(e.target.files?.[0])}
                        data-testid="input-adiantamento-camera"
                      />
                      <input
                        type="file"
                        accept="image/*,application/pdf"
                        id="adiantamento-upload-input"
                        hidden
                        onChange={(e) => uploadAdiantamentoFile(e.target.files?.[0])}
                        data-testid="input-adiantamento-upload"
                      />
                      <Button
                        type="button"
                        variant="outline"
                        className="gap-1.5 text-indigo-700 border-indigo-300 hover:bg-indigo-50"
                        onClick={() => document.getElementById('adiantamento-camera-input')?.click()}
                        disabled={uploadingAdiantamento}
                        data-testid="btn-adiantamento-camera"
                      >
                        <Camera className="h-4 w-4" /> {uploadingAdiantamento ? "Enviando..." : "Foto"}
                      </Button>
                      <Button
                        type="button"
                        variant="outline"
                        className="gap-1.5 text-purple-700 border-purple-300 hover:bg-purple-50"
                        onClick={() => document.getElementById('adiantamento-upload-input')?.click()}
                        disabled={uploadingAdiantamento}
                        data-testid="btn-adiantamento-upload"
                      >
                        <Upload className="h-4 w-4" /> Upload
                      </Button>
                    </div>
                  )}
                  <p className="text-[10px] text-gray-500 mt-1">Anexe o comprovante do PIX, recibo ou foto da nota — obrigatório.</p>
                </div>

                <div className="flex gap-2">
                  <Button type="button" variant="outline" onClick={() => setPagamentoDialogOpen(false)} className="flex-1">Fechar</Button>
                  <Button type="submit" disabled={uploadingAdiantamento || !formPagamento.imagem_path} className="flex-1 bg-green-500 hover:bg-green-600" data-testid="btn-adicionar-adiantamento">Adicionar</Button>
                </div>
              </form>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Dialog Recibos */}
      <Dialog open={recibosDialog} onOpenChange={setRecibosDialog}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" aria-describedby="recibos-desc">
          <DialogHeader>
            <DialogTitle>Recibos - {recibosCliente}</DialogTitle>
          </DialogHeader>
          <p id="recibos-desc" className="sr-only">Imagens dos comprovantes de pagamento</p>
          <div className="space-y-4">
            {recibosData.map((pag, idx) => (
              <div key={pag.id || idx} className={`border rounded-lg overflow-hidden ${pag.invalido ? 'border-red-300 bg-red-50' : ''}`}>
                <div className="bg-gray-50 px-3 py-2 flex justify-between items-center flex-wrap gap-2">
                  <div>
                    <p className="text-sm font-medium flex items-center gap-2">
                      {pag.forma_pagamento === 'dh_em_maos' ? 'DH EM MÃOS' : 
                       pag.forma_pagamento === 'pix' ? 'PIX' : 
                       pag.forma_pagamento === 'credito' ? 'CRÉDITO' : 
                       pag.forma_pagamento === 'debito' ? 'DÉBITO' : 
                       (pag.forma_pagamento || '').toUpperCase()}
                      {pag.invalido && <span className="px-1.5 py-0.5 bg-red-600 text-white text-[10px] font-bold rounded">INVALIDADO</span>}
                    </p>
                    <p className="text-xs text-gray-500">
                      R$ {(pag.valor || 0).toFixed(2)} | {pag.data ? pag.data.split('T')[0].split('-').reverse().join('/') : (pag.data_pagamento || '').split('-').reverse().join('/')}
                    </p>
                  </div>
                  <div className="flex gap-1">
                    {isAdmin && pag.id && (
                      <Button
                        variant={pag.invalido ? "outline" : "destructive"}
                        size="sm"
                        onClick={() => handleInvalidarComprovanteDividas(pag.id)}
                        className="gap-1"
                        data-testid={`btn-invalidar-recibo-${idx}`}
                        title={pag.invalido ? "Reativar comprovante" : "Invalidar comprovante (subtrai do valor pago)"}
                      >
                        <XCircle className="h-3 w-3" /> {pag.invalido ? "Reativar" : "Invalidar"}
                      </Button>
                    )}
                    {isAdmin && (
                      <Button variant="outline" size="sm" onClick={() => handleDownloadRecibo(pag.imagem_path)} className="gap-1" data-testid={`download-recibo-${idx}`}>
                        <Download className="h-3 w-3" /> Baixar
                      </Button>
                    )}
                  </div>
                </div>
                <RenderComprovante path={pag.imagem_path} alt={`Recibo ${idx + 1}`} />
              </div>
            ))}
            {recibosData.length === 0 && (
              <p className="text-center text-gray-500 py-4">Nenhum recibo com imagem encontrado.</p>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Dialog Foto Viewer */}
      <Dialog open={!!fotoViewer} onOpenChange={(open) => { if (!open) fecharFotoViewer(); }}>
        <DialogContent className="max-w-2xl" aria-describedby="foto-desc">
          <DialogHeader>
            <DialogTitle>Foto da Venda {fotoViewer && fotoViewer.fotos.length > 1 ? `(${fotoViewer.idx + 1}/${fotoViewer.fotos.length})` : ''}</DialogTitle>
          </DialogHeader>
          <p id="foto-desc" className="sr-only">Visualização da foto de comprovante da venda</p>
          {fotoViewer && (
            <div className="space-y-3">
              <div className="bg-gray-900 rounded-lg flex items-center justify-center min-h-[300px] max-h-[70vh] overflow-hidden">
                {loadingFoto || !fotoViewer.blobUrl ? (
                  <p className="text-white text-sm">Carregando...</p>
                ) : (
                  <img
                    src={fotoViewer.blobUrl}
                    alt="Foto da venda"
                    className="max-w-full max-h-[70vh] object-contain"
                    data-testid="foto-viewer-img"
                  />
                )}
              </div>
              <div className="flex justify-between items-center">
                <div className="text-xs text-gray-500">
                  {fotoViewer.fotos[fotoViewer.idx]?.enviado_por && <>Enviado por: <strong>{fotoViewer.fotos[fotoViewer.idx].enviado_por}</strong></>}
                  {fotoViewer.fotos[fotoViewer.idx]?.enviado_em && (
                    <> · {new Date(fotoViewer.fotos[fotoViewer.idx].enviado_em).toLocaleString('pt-BR', { timeZone: 'America/Campo_Grande' })}</>
                  )}
                </div>
                <div className="flex gap-2">
                  {fotoViewer.fotos.length > 1 && (
                    <>
                      <Button variant="outline" size="sm" onClick={() => navegarFoto(-1)} disabled={fotoViewer.idx === 0} data-testid="foto-prev">
                        Anterior
                      </Button>
                      <Button variant="outline" size="sm" onClick={() => navegarFoto(1)} disabled={fotoViewer.idx === fotoViewer.fotos.length - 1} data-testid="foto-next">
                        Próxima
                      </Button>
                    </>
                  )}
                  {isAdmin && (
                    <Button variant="outline" size="sm" onClick={excluirFoto} className="text-red-600 border-red-300 hover:bg-red-50 gap-1" data-testid="foto-excluir">
                      <Trash2 className="h-4 w-4" /> Excluir
                    </Button>
                  )}
                  <Button size="sm" onClick={fecharFotoViewer} data-testid="foto-fechar">Fechar</Button>
                </div>
              </div>
              <p className="text-[11px] text-gray-400 italic">Fotos expiram automaticamente em 40 dias.</p>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Dialog: Excluir nota (senha) */}
      <Dialog open={!!excluirDialog} onOpenChange={(o) => !o && setExcluirDialog(null)}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>Excluir Nota</DialogTitle>
            <DialogDescription>A nota poderá ser restaurada na lixeira em até 7 dias.</DialogDescription>
          </DialogHeader>
          {excluirDialog?.promissoria && (
            <div className="space-y-3">
              <p className="text-sm text-gray-700">
                Mover para a lixeira a nota <strong>#{excluirDialog.promissoria.numero_pedido}</strong> ({excluirDialog.promissoria.cliente_nome})?
              </p>
              <div className="bg-amber-50 border border-amber-200 rounded p-2.5 text-xs text-amber-900">
                A nota pode ser restaurada dentro de <strong>7 dias</strong>. Após esse prazo, será apagada definitivamente.
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="senha-excluir">Senha de segurança *</Label>
                <Input id="senha-excluir" type="password" value={senhaExcluir} onChange={e => setSenhaExcluir(e.target.value)} placeholder="Digite a senha" autoFocus data-testid="input-senha-excluir" onKeyDown={e => e.key === 'Enter' && confirmarExclusao()} />
                <p className="text-[11px] text-gray-500">Mesma senha do "Zerar Caixa"</p>
              </div>
              <div className="flex justify-end gap-2 pt-2">
                <Button variant="outline" onClick={() => setExcluirDialog(null)}>Cancelar</Button>
                <Button onClick={confirmarExclusao} disabled={excluindoLoading} className="bg-red-600 hover:bg-red-700 gap-1.5" data-testid="btn-confirmar-excluir">
                  <Trash2 className="h-4 w-4" /> {excluindoLoading ? "Excluindo…" : "Mover para Lixeira"}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Dialog: Lixeira */}
      <Dialog open={showLixeira} onOpenChange={setShowLixeira}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Trash2 className="h-5 w-5 text-gray-600" /> Lixeira de Notas
            </DialogTitle>
            <DialogDescription>Notas excluídas nos últimos 7 dias. Após esse prazo, são apagadas automaticamente.</DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            <p className="text-xs text-gray-500">Notas excluídas nos últimos 7 dias. Após esse prazo, são apagadas automaticamente.</p>
            {lixeira.length === 0 ? (
              <div className="text-center py-10 text-gray-400">
                <Trash2 className="h-10 w-10 mx-auto opacity-30" />
                <p className="mt-2 text-sm">Lixeira vazia</p>
              </div>
            ) : (
              lixeira.map(n => {
                const dExc = n.excluida_em ? new Date(n.excluida_em) : null;
                const expira = dExc ? new Date(dExc.getTime() + 7 * 86400000) : null;
                const diasRest = expira ? Math.ceil((expira - new Date()) / 86400000) : 0;
                return (
                  <div key={n.id} className="border rounded-lg p-3 space-y-1.5" data-testid={`lixeira-item-${n.id}`}>
                    <div className="flex justify-between items-start gap-2 flex-wrap">
                      <div className="min-w-0">
                        <p className="font-bold text-sm">#{n.numero_pedido} — {n.cliente_nome}</p>
                        <p className="text-[11px] text-gray-500">
                          Excluída em {dExc ? dExc.toLocaleString("pt-BR") : "-"} por <strong>{n.excluida_por_nome || "?"}</strong>
                        </p>
                        <p className="text-[11px] text-amber-700 font-medium">⏳ Será apagada em {diasRest} dia(s)</p>
                      </div>
                      <p className="text-sm font-bold text-gray-700">R$ {Number(n.valor_total || 0).toLocaleString("pt-BR", { minimumFractionDigits: 2 })}</p>
                    </div>
                    <div className="flex justify-end gap-2 pt-1">
                      <Button size="sm" variant="outline" onClick={() => restaurarDaLixeira(n)} className="text-emerald-700 border-emerald-300 gap-1.5" data-testid={`btn-restaurar-${n.id}`}>
                        <RotateCcw className="h-3.5 w-3.5" /> Restaurar
                      </Button>
                      <Button size="sm" variant="outline" onClick={() => setPurgaSenha({ open: n, senha: "" })} className="text-red-600 border-red-300 gap-1.5" data-testid={`btn-purgar-${n.id}`}>
                        <X className="h-3.5 w-3.5" /> Apagar agora
                      </Button>
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Dialog: Purga definitiva (confirmacao simples, sem senha) */}
      <Dialog open={!!purgaSenha.open} onOpenChange={(o) => !o && setPurgaSenha({ open: null, senha: "" })}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>Apagar Definitivamente</DialogTitle>
            <DialogDescription>Esta ação é irreversível. A nota será removida do sistema.</DialogDescription>
          </DialogHeader>
          {purgaSenha.open && (
            <div className="space-y-3">
              <p className="text-sm text-gray-700">
                Apagar para sempre a nota <strong>#{purgaSenha.open.numero_pedido}</strong>? Esta ação <strong>não pode ser desfeita</strong>.
              </p>
              <div className="flex justify-end gap-2">
                <Button variant="outline" onClick={() => setPurgaSenha({ open: null, senha: "" })}>Cancelar</Button>
                <Button onClick={() => purgarPermanente(purgaSenha.open)} className="bg-red-700 hover:bg-red-800" data-testid="btn-confirmar-purgar">Apagar</Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Dialog: Resetar Contador de Promissórias */}
      <Dialog open={resetContadorOpen} onOpenChange={setResetContadorOpen}>
        <DialogContent className="max-w-md" data-testid="dialog-resetar-contador">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-amber-700">
              <Hash className="h-5 w-5" /> Resetar Contador de Promissórias
            </DialogTitle>
            <DialogDescription>
              Define a partir de qual número as <strong>próximas</strong> notas serão criadas. Não afeta nenhuma promissória existente.
            </DialogDescription>
          </DialogHeader>

          {/* Status atual */}
          <div className="rounded-lg bg-slate-50 border border-slate-200 p-3 text-sm space-y-1">
            <p className="text-slate-600">Próximo número que será gerado agora:</p>
            <p className="text-2xl font-bold text-slate-900" data-testid="reset-next-number">
              {resetStatus ? String(resetStatus.next_number).padStart(4, '0') : '—'}
            </p>
            {resetStatus?.reset_ativo && (
              <p className="text-xs text-emerald-700 mt-1">
                ✓ Reset ativo desde {resetStatus.reset_em?.split('T')[0]} (começa de {String(resetStatus.comeca_de).padStart(4, '0')})
              </p>
            )}
          </div>

          <div className="space-y-3">
            <div>
              <Label className="text-sm">Começar do número</Label>
              <Input
                type="number"
                min="1"
                value={resetStartFrom}
                onChange={(e) => setResetStartFrom(e.target.value)}
                placeholder="1"
                data-testid="input-reset-start-from"
              />
              <p className="text-xs text-gray-500 mt-1">
                Ex: digite <strong>1</strong> para que a próxima nota seja nº 0001.
              </p>
            </div>
            <div>
              <Label className="text-sm">PIN de segurança</Label>
              <Input
                type="password"
                value={resetPin}
                onChange={(e) => setResetPin(e.target.value)}
                placeholder="Digite 000"
                data-testid="input-reset-pin"
              />
            </div>
          </div>

          <div className="rounded-lg bg-amber-50 border border-amber-200 p-2.5 text-xs text-amber-900">
            ⚠️ Suas notas antigas <strong>permanecem visíveis</strong>. Apenas o cálculo do próximo número muda. Pode desfazer a qualquer momento.
          </div>

          <div className="flex gap-2 justify-end flex-wrap">
            {resetStatus?.reset_ativo && (
              <Button
                variant="outline"
                onClick={desfazerResetContador}
                disabled={resetSaving}
                className="text-gray-700"
                data-testid="btn-desfazer-reset"
              >
                Desfazer reset
              </Button>
            )}
            <Button
              variant="outline"
              onClick={() => setResetContadorOpen(false)}
              data-testid="btn-cancelar-reset"
            >
              Cancelar
            </Button>
            <Button
              onClick={aplicarResetContador}
              disabled={resetSaving || !resetPin}
              className="bg-amber-600 hover:bg-amber-700 text-white"
              data-testid="btn-aplicar-reset"
            >
              {resetSaving ? 'Aplicando...' : 'Resetar contador'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function NotaAlteradaCard({ p, onAprovar, formatarMoeda }) {
  const resumo = Array.isArray(p.alteracao_resumo) ? p.alteracao_resumo : [];
  const dt = p.editada_em || p.alteracao_data_pendente;
  const dataFmt = dt ? (() => {
    try { const d = new Date(dt); return d.toLocaleString("pt-BR", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" }); } catch { return ""; }
  })() : "";
  const labelMudanca = {
    removido:    { ico: "❌", txt: "Removido",  cor: "text-red-700 bg-red-50 border-red-200" },
    diminuido:   { ico: "➖", txt: "Diminuído", cor: "text-orange-700 bg-orange-50 border-orange-200" },
    adicionado:  { ico: "➕", txt: "Adicionado", cor: "text-emerald-700 bg-emerald-50 border-emerald-200" },
    aumentado:   { ico: "⬆️", txt: "Aumentado", cor: "text-emerald-700 bg-emerald-50 border-emerald-200" },
    valor_total: { ico: "💰", txt: "Valor",     cor: "text-blue-700 bg-blue-50 border-blue-200" },
  };
  return (
    <div className="bg-white rounded-lg border border-amber-300 p-3 space-y-2" data-testid={`alterada-${p.id}`}>
      <div className="flex items-start justify-between gap-2 flex-wrap">
        <div className="flex-1 min-w-0">
          <p className="font-bold text-sm text-gray-900">#{p.numero_pedido} — {p.cliente_nome}</p>
          <p className="text-[11px] text-gray-500">
            Alterada por <strong>{p.editada_por_nome || "usuário"}</strong>{dataFmt ? ` · ${dataFmt}` : ""}
          </p>
        </div>
        <p className="text-base font-bold text-gray-900">{formatarMoeda(p.valor_total)}</p>
      </div>
      {resumo.length === 0 ? (
        <p className="text-[11px] italic text-gray-500">Sem detalhamento de mudanças disponível.</p>
      ) : (
        <div className="flex flex-wrap gap-1.5">
          {resumo.map((m, idx) => {
            const meta = labelMudanca[m.tipo] || { ico: "•", txt: m.tipo, cor: "text-gray-700 bg-gray-50 border-gray-200" };
            if (m.tipo === "valor_total") {
              return (
                <span key={idx} className={`text-[11px] px-2 py-1 rounded-full border ${meta.cor}`}>
                  {meta.ico} {meta.txt}: {formatarMoeda(m.valor_antes)} → {formatarMoeda(m.valor_depois)}
                </span>
              );
            }
            return (
              <span key={idx} className={`text-[11px] px-2 py-1 rounded-full border ${meta.cor}`}>
                {meta.ico} {meta.txt}: {m.produto_nome} ({m.quantidade}× {m.categoria})
              </span>
            );
          })}
        </div>
      )}
      <div className="flex justify-end">
        <Button
          size="sm"
          onClick={onAprovar}
          className="bg-emerald-600 hover:bg-emerald-700 gap-1.5"
          data-testid={`btn-emitir-nfe-${p.id}`}
        >
          ✅ Emitir NFe
        </Button>
      </div>
    </div>
  );
}

// ====== Histórico de Alterações (collapsible, abaixo do card de dívida) ======
function HistoricoAlteracoesBlock({ historico, promId, formatarMoeda }) {
  const [aberto, setAberto] = useState(false);
  if (!historico || historico.length === 0) return null;
  const ordenado = [...historico].sort((a, b) => (b?.data || "").localeCompare(a?.data || ""));
  const fmtData = (iso) => {
    if (!iso) return "";
    try {
      const d = new Date(iso);
      return d.toLocaleString("pt-BR", { day: "2-digit", month: "2-digit", year: "2-digit", hour: "2-digit", minute: "2-digit" });
    } catch { return ""; }
  };
  const tipoLabel = { admin: "Admin", secretario: "Secretário", vendedor: "Vendedor" };
  const mudancaLine = (m) => {
    if (m.tipo === "valor_total") {
      return `Valor: ${formatarMoeda(m.valor_antes)} → ${formatarMoeda(m.valor_depois)}`;
    }
    const verbo = { removido: "Removeu", diminuido: "Diminuiu", adicionado: "Adicionou", aumentado: "Aumentou" }[m.tipo] || m.tipo;
    return `${verbo} ${m.quantidade}× ${m.produto_nome}`;
  };
  return (
    <div className="mt-3 border-t pt-2" data-testid={`historico-bloco-${promId}`}>
      <button
        type="button"
        onClick={() => setAberto((v) => !v)}
        className="flex items-center gap-1.5 text-xs text-gray-500 hover:text-gray-700 font-medium"
        data-testid={`btn-toggle-historico-${promId}`}
      >
        <History className="h-3.5 w-3.5" />
        Histórico de alterações ({historico.length})
        <span className="text-[10px]">{aberto ? "▲" : "▼"}</span>
      </button>
      {aberto && (
        <ul className="mt-2 space-y-1.5">
          {ordenado.map((h, idx) => (
            <li key={idx} className="text-[11px] text-gray-700 bg-gray-50 border border-gray-200 rounded px-2 py-1.5" data-testid={`historico-item-${promId}-${idx}`}>
              <div className="flex justify-between gap-2 flex-wrap">
                <span>
                  <strong>{h.usuario_nome || "—"}</strong>
                  {h.usuario_tipo ? <span className="text-gray-400"> · {tipoLabel[h.usuario_tipo] || h.usuario_tipo}</span> : null}
                </span>
                <span className="text-gray-500">{fmtData(h.data)}</span>
              </div>
              {Array.isArray(h.mudancas) && h.mudancas.length > 0 ? (
                <ul className="mt-1 list-disc list-inside space-y-0.5">
                  {h.mudancas.map((m, i) => (
                    <li key={i} className="text-gray-700">{mudancaLine(m)}</li>
                  ))}
                </ul>
              ) : (
                <p className="text-gray-400 italic mt-1">Sem detalhamento de itens.</p>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
